import { Box, Button, Typography } from '@mui/material';
import React, { useEffect, useState, useMemo } from 'react';

import { useInstallationGuide } from '../../../hooks/useInstallationGuide';
import { SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_TEL } from '../../../utils/constants';
import { CORE_MODEL_INFO, isManuallySelectableOnRoute } from '../../../utils/coreModel';
import type { OSRoute } from '../../../utils/coreModel';
import { CORE_SETUP_KEYS } from '../../../utils/localStorage';
import { isCoreModel } from '../InstallationGuideProvider';
import type { CoreModel } from '../InstallationGuideProvider';
import { WizardNavigation } from '../WizardNavigation';

/**
 * Helper to read derived model from localStorage - reuses isCoreModel (the same
 * membership + route-availability check InstallationGuideProvider uses) so a stale or
 * cross-route stored value is rejected consistently, not via a separate ad-hoc check.
 */
function getDerivedModel(route: OSRoute): CoreModel | null {
  try {
    const item = localStorage.getItem(CORE_SETUP_KEYS.DERIVED_MODEL);
    if (!item) return null;

    const parsed = JSON.parse(item);
    if (parsed && typeof parsed === 'object' && 'value' in parsed) {
      // Check expiration
      if ('expiresAt' in parsed && typeof parsed.expiresAt === 'number') {
        if (Date.now() > parsed.expiresAt) {
          return null;
        }
      }

      return isCoreModel(parsed.value, route) ? parsed.value : null;
    }
    return null;
  } catch {
    return null;
  }
}

/** Path to the confirmation animation */
const RECEIVED_GIF_PATH = '/assets/images/installation-guide/received.gif';

/**
 * ConfirmModelStep - Slide 2.1: Confirm Core Model
 * 
 * Shows the device image based on deviceModel and allows the user to confirm.
 * Includes three conditional layers:
 * - Yes-Confirmed: Brief animation after confirmation
 * - Wrong-Model: Support contact when device doesn't match
 * - Select-Core: Manual model selection when deviceModel is null
 */
export const ConfirmModelStep: React.FC = () => {
  const {
    deviceModel,
    setDeviceModel,
    activeLayer,
    setActiveLayer,
    goToStep,
    setMainStepId,
    isV2Route,
  } = useInstallationGuide();
  const route = isV2Route ? 'v2' : 'v1';

  const [selectedModel, setSelectedModel] = useState<CoreModel | null>(null);
  const [derivedModel, setDerivedModel] = useState<CoreModel | null>(null);
  const deviceInfo = useMemo(() => {
    const model = deviceModel || derivedModel;
    if (model) {
      const info = CORE_MODEL_INFO[model];
      return { src: info.imageSrc, labelKey: info.full };
    }
    return null;
  }, [deviceModel, derivedModel]);

  useEffect(() => {
    setMainStepId('step_1_preInstall');

    if (!deviceModel) {
      setActiveLayer('select-core');
    }

    const derived = getDerivedModel(route);
    if (derived) {
      setDerivedModel(derived);
    }
  }, [setMainStepId, deviceModel, setActiveLayer, route]);

  const handleConfirm = () => {
    setActiveLayer('yes-confirmed');
  };

  const handleWrongModel = () => {
    setActiveLayer('wrong-model');
  };

  useEffect(() => {
    if (activeLayer === 'yes-confirmed') {
      const timer = setTimeout(() => {
        setActiveLayer(null);
        goToStep('readiness-check');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [activeLayer, setActiveLayer, goToStep]);

  const handleCallSupport = () => {
    window.location.href = SUPPORT_PHONE_TEL;
  };

  const handleReturnFromWrongModel = () => {
    setActiveLayer(null);
  };

  const handleModelSelect = (model: CoreModel) => {
    setSelectedModel(model);
  };

  const handleConfirmSelection = () => {
    if (selectedModel) {
      setDeviceModel(selectedModel);
      setActiveLayer(null);
      goToStep('readiness-check');
    }
  };


  // Render Yes-Confirmed layer (overlay with animation)
  if (activeLayer === 'yes-confirmed') {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 1200,
        }}
      >
        <Box
          component="img"
          src={RECEIVED_GIF_PATH}
          alt={__('Confirmed')}
          sx={{
            width: 120,
            height: 120,
          }}
        />
      </Box>
    );
  }

  // Render Wrong-Model layer
  if (activeLayer === 'wrong-model') {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              mb: 3, 
              textAlign: 'center',
              fontWeight: 500,
            }}
          >
            {__("Let's get this resolved.")}
          </Typography>

          <Typography 
            variant="body1" 
            sx={{ 
              mb: 4, 
              textAlign: 'center',
              color: 'text.secondary',
              px: 2,
            }}
          >
            {__('We may have an incorrect record for your Core device. Please contact our technical support team at $[phone].', {
              phone: SUPPORT_PHONE_DISPLAY,
            })}
          </Typography>
        </Box>

        <WizardNavigation
          primaryLabel={__('Call Axon Fusus Customer Support')}
          onPrimary={handleCallSupport}
          secondaryLabel={__('Return to previous screen')}
          onSecondary={handleReturnFromWrongModel}
        />
      </Box>
    );
  }

  // Render Select-Core layer (when deviceModel is null)
  if (activeLayer === 'select-core') {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h3" component="h1" sx={{ mb: 1, textAlign: 'center' }}>
          {__('Select your Core.')}
        </Typography>

        <Typography variant="h6" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
          {__('Which Core model did you receive?')}
        </Typography>

        {/* Model selection cards */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, my: 2 }}>
          {(Object.entries(CORE_MODEL_INFO) as [CoreModel, typeof CORE_MODEL_INFO[CoreModel]][])
            // Only offer models actually available on this route (v1/v2), excluding
            // limited-availability models (e.g. Mobile AI) that are auto-detection only.
            .filter(([model]) => isManuallySelectableOnRoute(model, route))
            .map(([model, info]) => (
              <Button
                key={model}
                onClick={() => handleModelSelect(model)}
                variant="outlined"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 2,
                  p: 2,
                  borderRadius: 2,
                  borderColor: selectedModel === model ? 'primary.main' : 'divider',
                  borderWidth: selectedModel === model ? 2 : 1,
                  bgcolor: selectedModel === model ? 'action.selected' : 'background.paper',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Box
                  component="img"
                  src={info.imageSrc}
                  alt={__(info.full)}
                  sx={{
                    width: 80,
                    height: 'auto',
                    maxHeight: 60,
                    objectFit: 'contain',
                  }}
                />
                <Typography variant="h6" sx={{ color: 'text.primary' }}>
                  {__(info.full)}
                </Typography>
              </Button>
            )
          )}
        </Box>

        <WizardNavigation
          primaryLabel={__('Confirm')}
          onPrimary={handleConfirmSelection}
          primaryDisabled={!selectedModel}
        />
      </Box>
    );
  }

  // Render base state (device model is known)
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h3" component="h1" sx={{ mb: 1, textAlign: 'center' }}>
        {__('Confirm your Core.')}
      </Typography>

      <Typography variant="h6" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
        {__('Is this the Core you received?')}
      </Typography>

      {/* Device image */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          my: 2,
        }}
      >
        {deviceInfo ? (
          <>
            <Box
              component="img"
              src={deviceInfo.src}
              alt={__(deviceInfo.labelKey)}
              sx={{
                maxWidth: '80%',
                maxHeight: 200,
                objectFit: 'contain',
                mb: 2,
              }}
            />
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.secondary',
                fontWeight: 500,
              }}
            >
              {__(deviceInfo.labelKey)}
            </Typography>
          </>
        ) : (
          <Typography color="text.secondary">
            {__('(Model not specified)')}
          </Typography>
        )}
      </Box>

      <WizardNavigation
        primaryLabel={__('Yes, it is.')}
        onPrimary={handleConfirm}
        secondaryLabel={__('No, I received a different model.')}
        onSecondary={handleWrongModel}
      />
    </Box>
  );
};
